import {Component} from "@angular/core";
import {DialogComponent} from "../../bootstrap-modal/dialog.component";
import {DialogService} from "../../bootstrap-modal/dialog.service";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {
  SiteCdfBuildingModel,
  siteCdfBuildingModelFactory,
  SiteCdfModel,
  siteCdfModelFactory,
} from "../../sites/site-detail.model";
import {CdfImportService} from "../../cdf/cdf-import.service";
import {toList} from "../../helpers/helpers";
import {IDropdownSettings} from "ng-multiselect-dropdown";

@Component({
  templateUrl: "./site-cdf-dialog.template.pug",
  providers: [CdfImportService],
})
export class SiteCdfDialogComponent extends DialogComponent<SiteCdfModel, SiteCdfModel> implements SiteCdfModel {
  error: Error;
  canRemove: boolean = false;
  id: string;
  system: string;
  buildings: string[] = [];
  selectableBuildings: SiteCdfBuildingModel[];
  canSave: boolean = false;
  dropdownSiteSettings: IDropdownSettings = {
    singleSelection: false,
    idField: "index",
    textField: "site",
    selectAllText: "Select All",
    unSelectAllText: "UnSelect All",
    allowSearchFilter: false,
    itemsShowLimit: 1,
    noDataAvailablePlaceholderText: "No sites to show",
  };

  dropdownBuildingSettings: IDropdownSettings = {
    singleSelection: false,
    idField: "index",
    textField: "building",
    selectAllText: "Select All",
    unSelectAllText: "UnSelect All",
    allowSearchFilter: false,
    itemsShowLimit: 1,
    noDataAvailablePlaceholderText: "No buildings to show",
  };

  dropdownFloorSettings: IDropdownSettings = {
    singleSelection: false,
    idField: "index",
    textField: "floor",
    selectAllText: "Select All",
    unSelectAllText: "UnSelect All",
    allowSearchFilter: false,
    itemsShowLimit: 1,
    noDataAvailablePlaceholderText: "No floors to show",
  };

  siteList = [];
  buildingList = [];
  floorList = [];
  showBuildings = [];
  itemsSelected = {
    sites: [],
    buildings: [],
    floor: [],
  };

  static showCreateDialog(dialogService: DialogService, cdf: SiteCdfModel): Promise<SiteCdfModel> {
    return dialogService.showDialog(SiteCdfDialogComponent, cdf as SiteCdfModel);
  }

  cancel(): void {
    this.close();
  }

  ready() {
    this.result = siteCdfModelFactory(this);
    if (this.id) this.canRemove = true;
  }

  updateSitesView() {
    this.buildingList = this.selectableBuildings.filter(building => this.itemsSelected.sites.includes(building.site));
    this.showBuildings = this.itemsSelected.sites.length ?
      this.selectableBuildings.filter(building => this.itemsSelected.sites.includes(building.site)) :
      this.selectableBuildings;
    this.buildingList = [...new Set(this.buildingList.map(b => b.building).sort())].map((c, index) => ({
      index: `building${index}`,
      building: c,
    }));
    this.floorList = this.selectableBuildings.filter(building => this.itemsSelected.sites.includes(building.site));
    this.floorList = [...new Set(this.floorList.map(b => b.floor).sort())].map((c, index) => ({
      index: `building${index}`,
      floor: c,
    }));
  }

  updateBuildingView() {
    this.floorList =
      this.selectableBuildings.filter(building => this.itemsSelected.buildings.includes(building.building) &&
        this.itemsSelected.sites.includes(building.site));
    this.showBuildings = this.selectableBuildings.filter(building => this.itemsSelected.sites.includes(building.site));
    if (this.itemsSelected.buildings.length) {
      this.showBuildings =
        this.showBuildings.filter(building => this.itemsSelected.buildings.includes(building.building));
    }
    this.floorList = [...new Set(this.floorList.map(b => b.floor).sort())].map((c, index) => ({
      index: `building${index}`,
      floor: c,
    }));
  }

  updateFloorView() {
    this.showBuildings = this.selectableBuildings.filter(building => this.itemsSelected.sites.includes(building.site) &&
      this.itemsSelected.buildings.includes(building.building));
    if (this.itemsSelected.floor.length) {
      this.showBuildings = this.showBuildings.filter(building => this.itemsSelected.floor.includes(building.floor));
    }
  }

  onDeselectAllSite() {
    this.itemsSelected.sites = [];
    this.updateSitesView();
  }

  onDeselectAllBuilding() {
    this.itemsSelected.buildings = [];
    this.updateBuildingView();
  }

  onDeselectAllFloor() {
    this.itemsSelected.floor = [];
    this.updateFloorView();
  }

  onSiteSelect(item: any) {
    this.itemsSelected.sites.push(item.site);
    this.updateSitesView();
  }

  onBuildingSelect(item: any) {
    this.itemsSelected.buildings.push(item.building);
    this.updateBuildingView();
  }

  onFloorSelect(item: any) {
    this.itemsSelected.floor.push(item.floor);
    this.updateFloorView();
  }

  onSiteDeselect(item: any) {
    const index = this.itemsSelected.sites.findIndex(sites => sites === item.site);
    this.itemsSelected.sites.splice(index, 1);
    this.updateSitesView();
  }

  onBuildingDeselect(item: any) {
    const index = this.itemsSelected.buildings.findIndex(sites => sites === item.building);
    this.itemsSelected.buildings.splice(index, 1);
    this.updateBuildingView();
  }

  onFloorDeselect(item: any) {
    const index = this.itemsSelected.floor.findIndex(sites => sites === item.floor);
    this.itemsSelected.floor.splice(index, 1);
    this.updateFloorView();
  }

  onSelectAllSites(items: any) {
    this.itemsSelected.sites = items.map(site => site.site);
    this.updateSitesView();
  }

  onSelectAllBuildings(items: any) {
    this.itemsSelected.buildings = items.map(site => site.building);
    this.updateBuildingView();
  }

  onSelectAllFloors(items: any) {
    this.itemsSelected.floor = items.map(site => site.floor);
    this.updateFloorView();
  }

  clearBuildings() {
    this.selectableBuildings = [];
    this.canSave = false;
  }

  loadSelects() {
    this.showBuildings = this.selectableBuildings;
    this.selectableBuildings.forEach((building) => {
      if (!this.siteList.find(site => site.site === building.site)) {
        this.siteList.push(building);
      }
    });
    this.buildingList = [];
    this.floorList = [];
  }

  @handleErrors()
  async loadCdfDetails() {
    if (!this.id) {
      this.error = new Error("The value in CDF ID is required.");
      return;
    }

    const preview = await this.cdfImportService.getCdfPreview(this.id, this.system);
    this.selectableBuildings = toList(siteCdfBuildingModelFactory, preview.buildings).map((building, index) => ({
      ...building,
      index: `building${index}`,
    }));
    this.loadSelects();

    this.canSave = true;
  }

  save() {
    if (!this.canSave) return;
    this.buildings = this.selectableBuildings.filter(it => it.selected).map(it => it.building);
    this.result = siteCdfModelFactory(this);
    this.close();
  }

  remove() {
    this.result = siteCdfModelFactory({});
    this.close();
  }

  constructor(public dialogService: DialogService, private cdfImportService: CdfImportService) {
    super(dialogService);
  }
}
